import React from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const VenueDetailsSkeleton = () => {
    return (
        <div className="container mx-auto px-4 py-8">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 mb-6">
                {['Home', 'Shop', 'Electronics', 'Product'].map((item, index) => (
                    <div key={index}>
                        <Skeleton className="h-4 w-16" />
                        {/* {index < 3 && <Skeleton className="h-4 w-4" />} */}
                    </div>
                ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Product Images Section */}
                <div className="lg:w-1/2">
                    <div className="grid grid-cols-1 gap-4">
                        <Skeleton className="aspect-square w-full rounded-lg" />
                        <div className="grid grid-cols-3 gap-2">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={i} className="aspect-square rounded-md" />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Product Info Section */}
                <div className="lg:w-1/2 space-y-6">
                    {/* Badges */}
                    <div className="flex gap-2">
                        <Skeleton className="h-6 w-12 rounded-full" />
                        <Skeleton className="h-6 w-12 rounded-full" />
                        <Skeleton className="h-6 w-24 rounded-full" />
                    </div>

                    {/* Title */}
                    <Skeleton className="h-8 w-3/4" />

                    {/* Price */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-6 w-20" />
                            <Skeleton className="h-5 w-24" />
                        </div>
                        <Skeleton className="h-5 w-36" />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                        <Skeleton className="h-4 w-4/5" />
                    </div>

                    {/* Color Options */}
                    <div className="space-y-2">
                        <Skeleton className="h-5 w-16" />
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={i} className="h-10 w-10 rounded-full" />
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                        <Skeleton className="h-12 w-24 rounded-md" />
                        <Skeleton className="h-12 w-24 rounded-md" />
                    </div>

                    {/* Rental Options */}
                    <div className="space-y-4 pt-4">
                        <Skeleton className="h-5 w-36" />
                        <div className="flex items-center gap-4">
                            <Skeleton className="h-10 w-10 rounded-md" />
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="h-10 w-10 rounded-md" />
                        </div>
                        <Skeleton className="h-5 w-48" />
                    </div>

                    {/* Quantity Selector */}
                    <div className="space-y-2">
                        <Skeleton className="h-5 w-16" />
                        <div className="flex items-center gap-4">
                            <Skeleton className="h-10 w-10 rounded-md" />
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="h-10 w-10 rounded-md" />
                        </div>
                    </div>

                    {/* Add to Cart */}
                    <Skeleton className="h-12 w-full rounded-md mt-4" />

                    {/* Stock & Shipping Info */}
                    <div className="space-y-2 pt-4">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-4 w-56" />
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex border-b mt-12 gap-6">
                {['Description', 'Specifications', 'Reviews'].map((tab, i) => (
                    <Skeleton key={i} className="h-10 w-24 mb-[-1px]" />
                ))}
            </div>

            {/* Tab Content */}
            <div className="py-6 space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/4" />
            </div>

            {/* Key Features */}
            <div className="space-y-4">
                <Skeleton className="h-6 w-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <Skeleton className="h-4 w-4 rounded-full" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Extended Description */}
            <div className="space-y-4 pt-6">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
            </div>

            {/* Related Products */}
            <div className="mt-16">
                <Skeleton className="h-8 w-48 mb-6" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                        <Card key={i}>
                            <Skeleton className="aspect-square w-full rounded-t-lg" />
                            <div className="p-4 space-y-3">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-4 w-16" />
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-5 w-20" />
                                    {i % 2 === 0 && <Skeleton className="h-4 w-24" />}
                                </div>
                                <Skeleton className="h-10 w-full rounded-md" />
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VenueDetailsSkeleton;